import React, { useEffect, useState, useCallback } from 'react';
import { InstallInstallationCost } from '../../../../models/InstallModels';
import { InstallationCostFieldConfig } from './InstallTabFieldConfig';
import GenericFormTab from '../../../../components/common/GenericFormTab';

interface InstallInstallationCostTabProps {
  value: InstallInstallationCost;
  onChange: (value: InstallInstallationCost) => void;
  IsPurchasedSystem: boolean;
  startDate: Date | null;
}

function dateOrNull(val: any): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  const d = new Date(val);
  return isNaN(d.getTime()) ? null : d;
}

function calculateCosts(
  { DailyCost, RunlifeDays, TransferOwnershipDate, RepairCost, WorkoverCost }: InstallInstallationCost,
  isPurchased: boolean,
  startDate: Date | null,
) {
  const dailyCost = Number(DailyCost) || 0;
  const runlifeDays = Number(RunlifeDays) || 0;
  const repairCost = Number(RepairCost) || 0;
  const workoverCost = Number(WorkoverCost) || 0;
  const purchaseCost = 0;
  const monthlyCost = !isPurchased && dailyCost > 0 ? Math.round(dailyCost * 30.4 * 100) / 100 : 0;
  let transferOwnershipDays: number | null = null;
  const transferOwnershipDate = dateOrNull(TransferOwnershipDate);
  const _startDate = dateOrNull(startDate);
  if (transferOwnershipDate && _startDate) {
    transferOwnershipDays = Math.floor((transferOwnershipDate.getTime() - _startDate.getTime()) / (1000 * 3600 * 24));
  }
  let effectiveRunlifeDays = runlifeDays;
  if (typeof transferOwnershipDays === 'number' && transferOwnershipDays < runlifeDays) {
    effectiveRunlifeDays = transferOwnershipDays;
  }
  const rentalCost = !isPurchased && dailyCost > 0 ? Math.round(dailyCost * effectiveRunlifeDays * 100) / 100 : 0;
  const totalCost = !isPurchased ? workoverCost + rentalCost + repairCost : workoverCost + purchaseCost + repairCost;
  return { monthlyCost, rentalCost, totalCost };
}

const InstallInstallationCostTab: React.FC<InstallInstallationCostTabProps> = ({
  value,
  onChange,
  IsPurchasedSystem,
  startDate,
}) => {
  const [formState, setFormState] = useState<InstallInstallationCost>({ ...value });

  useEffect(() => {
    const { monthlyCost, rentalCost, totalCost } = calculateCosts(formState, IsPurchasedSystem, startDate);
    if (
      formState.MonthlyCost !== monthlyCost ||
      formState.RentalCost !== rentalCost ||
      formState.TotalCost !== totalCost
    ) {
      const updated = {
        ...formState,
        MonthlyCost: monthlyCost,
        RentalCost: rentalCost,
        TotalCost: totalCost,
      };
      setFormState(updated);
      onChange(updated);
    } else {
      onChange(formState);
    }
  }, [
    formState.DailyCost,
    formState.RunlifeDays,
    formState.TransferOwnershipDate,
    formState.RepairCost,
    formState.WorkoverCost,
    IsPurchasedSystem,
    startDate,
    onChange,
  ]);

  const handleFormChange = useCallback((updated: InstallInstallationCost) => {
    setFormState(updated);
  }, []);

  return (
    <GenericFormTab formData={formState} setFormData={handleFormChange} fieldConfigs={InstallationCostFieldConfig} />
  );
};

export default InstallInstallationCostTab;
